<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore } from '../stores/auth'

const authStore = useAuthStore()
const isDeleting = ref(false)
const isDeleted = ref(false)
const error = ref('')
const confirmText = ref('')

const canDelete = computed(() => confirmText.value === 'DELETE')

async function requestDeletion() {
  if (!canDelete.value) return

  isDeleting.value = true
  error.value = ''

  try {
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://cabin-calendar-api.constantine-macris.workers.dev'}/api/users/delete-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete account')
    }

    isDeleted.value = true
    // Clear local auth state
    authStore.logout()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'An error occurred'
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 py-8 px-4">
    <div class="max-w-2xl mx-auto bg-white rounded-lg shadow-sm p-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-6">Data Deletion</h1>

      <!-- Success State -->
      <div v-if="isDeleted" class="text-center py-8">
        <div class="text-6xl mb-4">&#10003;</div>
        <h2 class="text-2xl font-semibold text-green-600 mb-4">Account Deleted</h2>
        <p class="text-gray-600 mb-6">
          Your account and all associated data have been permanently deleted from our system.
        </p>
        <p class="text-gray-500 text-sm">
          If you signed in with Facebook, you may also want to remove the app from your
          <a href="https://www.facebook.com/settings?tab=applications" target="_blank" class="text-primary-600 hover:underline">Facebook Settings</a>.
        </p>
        <router-link to="/login" class="inline-block mt-6 text-primary-600 hover:underline">
          Return to Login
        </router-link>
      </div>

      <!-- Deletion Form -->
      <div v-else>
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p class="text-yellow-800 font-medium">Warning: This action cannot be undone</p>
        </div>

        <div class="space-y-4 text-gray-600 mb-6">
          <p>Requesting data deletion will permanently remove:</p>
          <ul class="list-disc list-inside space-y-2 ml-4">
            <li>Your account information (name, email, profile picture)</li>
            <li>All your booking history</li>
            <li>Your Code of Conduct agreement record</li>
            <li>Any pending booking requests</li>
          </ul>
        </div>

        <div v-if="authStore.isAuthenticated" class="space-y-4">
          <p class="text-gray-600">
            You are logged in as <strong>{{ authStore.user?.email }}</strong>
          </p>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Type DELETE to confirm:
            </label>
            <input
              v-model="confirmText"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="DELETE"
            />
          </div>

          <div v-if="error" class="text-red-600 text-sm">{{ error }}</div>

          <button
            @click="requestDeletion"
            :disabled="!canDelete || isDeleting"
            class="w-full py-3 px-4 bg-red-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
          >
            {{ isDeleting ? 'Deleting...' : 'Permanently Delete My Account' }}
          </button>
        </div>

        <div v-else class="space-y-4">
          <p class="text-gray-600">
            To delete your account, please log in first, or contact us directly:
          </p>
          <p>
            <a href="mailto:cabin@macris.co?subject=Data%20Deletion%20Request" class="text-primary-600 hover:underline">
              cabin@macris.co
            </a>
          </p>
          <p class="text-gray-500 text-sm">
            Include your email address in the request and we will process the deletion within 30 days.
          </p>
          <router-link to="/login" class="inline-block mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            Log In to Delete Account
          </router-link>
        </div>

        <div class="mt-8 pt-6 border-t border-gray-200">
          <h2 class="text-lg font-semibold text-gray-800 mb-3">Facebook Users</h2>
          <p class="text-gray-600 text-sm">
            If you logged in with Facebook, deleting your account here will remove all data we store.
            To also revoke our app's access to your Facebook account, visit your
            <a href="https://www.facebook.com/settings?tab=applications" target="_blank" class="text-primary-600 hover:underline">Facebook App Settings</a>.
          </p>
        </div>

        <div class="mt-6 pt-6 border-t border-gray-200">
          <router-link to="/privacy" class="text-primary-600 hover:underline">&larr; Back to Privacy Policy</router-link>
        </div>
      </div>
    </div>
  </div>
</template>
